import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'

/**
 * TextEffectsSecondaryBar
 * -----------------------
 * Picker for the Text Effects system (78 presets across 10 categories). Unlike
 * Animate/Motion (a single config), text effects STACK: item.textEffects is an
 * ARRAY and each preset toggles its entry in/out of that array. Fine-grained
 * params are edited in the Text Effects properties panel.
 *
 * Writes item.textEffects = [{ type, enabled, ...params }, ...]. Preset ids MUST
 * stay in sync with textEffectsEngine.js / text_effects.py.
 */
export default function TextEffectsSecondaryBar({
  selectedElement,
  template_content,
  settemplate_content,
  onClose,
}) {
  const [appliedTypes, setAppliedTypes] = useState([])
  const [hovered, setHovered] = useState(null)

  useEffect(() => {
    if (!selectedElement || !template_content) return
    const el = template_content.pages
      ?.flatMap((p) => p.items || [])
      ?.find((it) => it.id === selectedElement)
    const fx = Array.isArray(el?.textEffects) ? el.textEffects : []
    setAppliedTypes(fx.map((e) => e.type))
  }, [selectedElement, template_content])

  // Per-type default params (mirror the engine fallbacks). Only the primary
  // knobs are seeded; the engine fills the rest.
  const getEffectDefaults = (type) => {
    const d = {
      // Cat 1 — Basic Styling
      dropShadow: { color: '#000000', opacity: 0.6, offsetX: 4, offsetY: 4, blur: 6, spread: 0 },
      stroke: { color: '#ffffff', width: 3, align: 'outside', opacity: 1 },
      innerShadow: { color: '#000000', opacity: 0.6, angle: 135, depth: 4, blur: 4 },
      innerGlow: { color: '#ffffff', intensity: 0.8, radius: 8, opacity: 0.9, edge: 4 },
      backgroundBox: { color: '#000000', opacity: 0.5, radius: 6, padding: 10, border: 0, borderColor: '#ffffff' },
      textFill: { mode: 'solid', color: '#000000', opacity: 1 },
      longShadow: { color: '#000000', opacity: 0.4, angle: 45, length: 30, mode: 'solid' },
      multipleOutlines: { outlines: [{ color: '#000000', width: 6 }, { color: '#ff0000', width: 10 }] },
      doubleShadow: { near: { color: '#000000', offsetX: 2, offsetY: 2, blur: 2, opacity: 0.7 }, far: { color: '#000000', offsetX: 10, offsetY: 12, blur: 14, opacity: 0.4 } },
      underlineOverline: { lines: [{ position: 'under', color: '#000000', thickness: 3, offset: 2 }] },
      // Cat 2 — Shadows / Depth / 3D
      outerGlow: { color: '#66ccff', radius: 12, intensity: 1, spread: 2, opacity: 0.9, layers: 2 },
      neonGlow: { coreColor: '#ff2d95', tubeWidth: 2, halo: 24, brightness: 1, flicker: 0 },
      bevelEmboss: { depth: 3, width: 4, mode: 'inner', lightAngle: 135, highlight: '#ffffff', shadow: '#000000', softness: 2 },
      extrude3D: { depth: 12, direction: 45, sideColor: '#555555', frontColor: '', shading: 1 },
      metallic: { metal: 'gold', reflectivity: 0.8, bevel: 0 },
      glass: { transparency: 0.25, bgBlur: 6, tint: '#ffffff', edgeHighlight: '#ffffff' },
      reflection: { direction: 'down', distance: 4, opacity: 0.4, fade: 0.6, blur: 1, flip: true },
      extrudedOutline: { outlineWidth: 6, depth: 10, frontColor: '#ffffff', sideColor: '#333333', direction: 45 },
      // Cat 3 — Lighting / Highlights
      lightSweep: { color: '#ffffff', width: 0.25, angle: 20, brightness: 0.9, feather: 0.5, speed: 0.4 },
      lensFlare: { x: 0.8, y: 0.3, brightness: 1, color: '#ffffff', size: 0.4, streak: 0.6, opacity: 0.9 },
      edgeGlow: { color: '#00e5ff', edgeWidth: 3, brightness: 1, blur: 3, mode: 'inner' },
      animatedGlow: { minIntensity: 0.4, maxIntensity: 1, radius: 16, speed: 1, color: '#66ccff', flicker: 0 },
      sparkle: { count: 6, size: 6, brightness: 1, color: '#ffffff', duration: 1.2, seed: 1 },
      lightRays: { x: 0.5, y: 0.5, length: 1, width: 0.5, intensity: 0.8, color: '#ffffff', rotation: 0 },
      // Cat 4 — Gradients / Color
      linearGradient: { stops: [{ color: '#ff3b3b', position: 0 }, { color: '#ff9e3b', position: 100 }], angle: 90, opacity: 1 },
      radialGradient: { stops: [{ color: '#ffee88', position: 0 }, { color: '#d9731f', position: 100 }], centerX: 0.5, centerY: 0.5, radius: 0.7 },
      animatedGradient: { stops: [{ color: '#b31217', position: 0 }, { color: '#e52d27', position: 50 }, { color: '#ffd200', position: 100 }], angle: 90, speed: 0.3 },
      duotone: { shadowColor: '#13294b', highlightColor: '#ff7a1a', contrast: 1, threshold: 0.5 },
      colorOverlay: { color: '#ff0000', opacity: 1, blend: 'normal', scope: 'fill' },
      colorShift: { stops: [{ color: '#ffffff', position: 0 }, { color: '#ff0000', position: 100 }], duration: 2, easing: 'easeInOut', loop: true },
      duotoneTexture: { shadowColor: '#3b2a1a', highlightColor: '#f5e6c8', contrast: 1, scale: 1 },
      // Cat 5 — Material / Surface
      stoneMarble: { scale: 1, contrast: 1, bevel: 2, highlight: '#ffffff', shadow: '#000000' },
      woodTexture: { grainDir: 'horizontal', scale: 1, contrast: 1, tint: '' },
      paperFabric: { texture: 'paper', scale: 1, opacity: 1, edgeDistress: 0 },
      holographic: { palette: ['#ff00cc', '#3333ff', '#00ffee', '#ff00cc'], highlightDir: 60, reflection: 0.6, animShift: false },
      chrome: { bandWidth: 0.2, contrast: 1.2, movingHighlight: false },
      liquidMetal: { distortion: 4, rippleScale: 40, animSpeed: 0.5 },
      embossedPrint: { depth: 3, lightDir: 135, highlight: '#ffffff', shadow: '#000000', softness: 2 },
      // Cat 6 — Distortion / Digital
      gaussianBlur: { radius: 4, animated: false },
      directionalBlur: { angle: 0, distance: 12, opacity: 1 },
      motionBlurFx: { shutter: 180, samples: 6, intensity: 1 },
      glitchDistort: { strength: 8, blockSize: 12, burstFreq: 4, noise: 0.3, rgbSeparation: 4, seed: 1 },
      rgbSplit: { intensity: 3, gOffset: 0, direction: 'horizontal' },
      displacement: { strengthX: 8, strengthY: 8, scale: 0.1, speed: 0, seed: 1 },
      waveDistort: { amplitude: 8, wavelength: 40, direction: 'vertical', frequency: 1, speed: 0.5 },
      pixelate: { size: 8, smoothing: false, colorDepth: 0 },
      noiseStatic: { amount: 0.3, scale: 1, mode: 'mono', speed: 12, seed: 1 },
      edgeDistort: { roughness: 0.5, strength: 4, edgeWidth: 3, scale: 0.4, speed: 0, seed: 1 },
      // Cat 7 — Texture / Artistic
      filmGrain: { size: 1, intensity: 0.2, mono: true, speed: 24, contrast: 1 },
      halftone: { dotSize: 4, spacing: 6, angle: 45, contrast: 1, palette: ['#000000'], shape: 'circle' },
      comicBook: { outline: 4, halftoneScale: 4, palette: ['#000000'], contrast: 1.2 },
      distressed: { density: 0.3, size: 2, intensity: 0.6, edgeWear: 0.4, seed: 1 },
      cracked: { density: 0.4, width: 1, contrast: 1, glow: '', seed: 1 },
      burnedCharred: { intensity: 0.5, edgeColor: '#ff5a1a' },
      watercolorInk: { bleed: 0.5, edgeSoftness: 4, colorVar: '', seed: 1 },
      paintBrush: { texture: 'brush', strokeDir: 'horizontal', edgeRough: 0.4, seed: 1 },
      // Cat 8 — Masks / Compositing
      alphaMask: { source: '', invert: false, feather: 0, opacity: 1 },
      lumaMatte: { source: '', threshold: 0.5, contrast: 1, invert: false, feather: 0 },
      blendMode: { mode: 'multiply', opacity: 1 },
      bgBlurThroughText: { radius: 8, textOpacity: 0.15, tint: '#ffffff', edgeHighlight: true },
      textBehindSubject: { subjectMask: '', refine: 0, feather: 1 },
      textCutout: { source: '', invert: false, edgeSoftness: 0 },
      // Cat 9 — Animated Visual
      fireOverlay: { color: '#ff6a00', intensity: 1, speed: 1, spread: 1, glow: 1, mode: 'outside' },
      smokeOverlay: { density: 0.6, direction: 'up', speed: 1, color: '#888888', opacity: 0.5, maskMode: 'over' },
      sparksEmbers: { count: 20, rate: 20, speed: 1, lifetime: 1.5, color: '#ffb24d', glow: 1, size: 2, seed: 1 },
      lightning: { intensity: 1, branches: 3, color: '#8ad8ff', flicker: 10, glow: 1, region: 'outside', seed: 1 },
      waterRipple: { amplitude: 6, wavelength: 30, speed: 0.6, direction: 'vertical', reflection: 0 },
      heatHaze: { strength: 4, waveScale: 0.08, speed: 0.4, seed: 1 },
      particleTrail: { rate: 20, lifetime: 1, trail: 1, speed: 1, color: '#ffffff', glow: 1, size: 3 },
      animatedOutlineDraw: { color: '#ffffff', width: 2, speed: 0.5, glow: 0, fillReveal: 0.8 },
      // Cat 10 — Advanced
      liquidDistortion: { amount: 10, rippleScale: 0.1, speed: 0.4, smoothness: 1, seed: 1 },
      morphingText: { targetText: '', duration: 1, easing: 'easeInOut', distortion: 0 },
      fractureShatter: { fragments: 12, size: 1, direction: 90, force: 1, rotation: 1, gravity: 1, duration: 1.5, seed: 1 },
      burnAway: { direction: 'up', progress: 0, edgeGlow: 1, burnColor: '#ff5a1a', noiseScale: 0.3, duration: 2, seed: 1 },
      dissolveNoise: { noiseScale: 0.3, direction: 'none', progress: 0, edgeSoftness: 0.05, fragmentSize: 3, duration: 2, seed: 1 },
      animatedTextureFill: { source: '', scale: 1, speed: 1, blend: 'normal', fit: 'cover' },
      edgeExtrudeLighting: { depth: 10, bevelWidth: 3, lightDir: 135, lightColor: '#ffffff', material: '#444444', roughness: 0 },
      reflectionRefraction: { reflOpacity: 0.4, reflBlur: 2, distortion: 3, fade: 0.6 },
    }
    return d[type] || {}
  }

  const categories = {
    'Basic Styling': [
      ['dropShadow', 'Drop Shadow'], ['stroke', 'Stroke / Outline'], ['innerShadow', 'Inner Shadow'],
      ['innerGlow', 'Inner Glow'], ['backgroundBox', 'Background Box'], ['textFill', 'Text Fill'],
      ['longShadow', 'Long Shadow'], ['multipleOutlines', 'Multiple Outlines'], ['doubleShadow', 'Double Shadow'],
      ['underlineOverline', 'Underline / Overline'],
    ],
    'Shadows, Depth & 3D': [
      ['outerGlow', 'Outer Glow'], ['neonGlow', 'Neon Glow'], ['bevelEmboss', 'Bevel & Emboss'],
      ['extrude3D', '3D Extrusion'], ['metallic', 'Metallic'], ['glass', 'Glass / Frosted'],
      ['reflection', 'Reflection'], ['extrudedOutline', 'Extruded Outline'],
    ],
    'Lighting & Highlights': [
      ['lightSweep', 'Light Sweep'], ['lensFlare', 'Lens Flare'], ['edgeGlow', 'Edge Glow'],
      ['animatedGlow', 'Animated Glow'], ['sparkle', 'Sparkle & Glint'], ['lightRays', 'Light Rays'],
    ],
    'Gradients & Color': [
      ['linearGradient', 'Linear Gradient'], ['radialGradient', 'Radial Gradient'], ['animatedGradient', 'Animated Gradient'],
      ['duotone', 'Duotone'], ['colorOverlay', 'Color Overlay'], ['colorShift', 'Color Shift'], ['duotoneTexture', 'Duotone Texture'],
    ],
    'Material & Surface': [
      ['stoneMarble', 'Stone / Marble'], ['woodTexture', 'Wood'], ['paperFabric', 'Paper / Fabric'],
      ['holographic', 'Holographic'], ['chrome', 'Chrome'], ['liquidMetal', 'Liquid Metal'], ['embossedPrint', 'Embossed Print'],
    ],
    'Distortion & Digital': [
      ['gaussianBlur', 'Gaussian Blur'], ['directionalBlur', 'Directional Blur'], ['motionBlurFx', 'Motion Blur'],
      ['glitchDistort', 'Glitch'], ['rgbSplit', 'RGB Split'], ['displacement', 'Displacement'],
      ['waveDistort', 'Wave Distort'], ['pixelate', 'Pixelate'], ['noiseStatic', 'Noise / Static'], ['edgeDistort', 'Edge Distort'],
    ],
    'Texture & Artistic': [
      ['filmGrain', 'Film Grain'], ['halftone', 'Halftone'], ['comicBook', 'Comic Book'],
      ['distressed', 'Distressed'], ['cracked', 'Cracked'], ['burnedCharred', 'Burned / Charred'],
      ['watercolorInk', 'Watercolor / Ink'], ['paintBrush', 'Paint / Brush'],
    ],
    'Masks & Compositing': [
      ['alphaMask', 'Alpha Mask'], ['lumaMatte', 'Luma Matte'], ['blendMode', 'Blend Mode'],
      ['bgBlurThroughText', 'BG Blur Through Text'], ['textBehindSubject', 'Text Behind Subject'], ['textCutout', 'Text Cutout'],
    ],
    'Animated Visual': [
      ['fireOverlay', 'Fire'], ['smokeOverlay', 'Smoke'], ['sparksEmbers', 'Sparks & Embers'],
      ['lightning', 'Lightning'], ['waterRipple', 'Water Ripple'], ['heatHaze', 'Heat Haze'],
      ['particleTrail', 'Particle Trail'], ['animatedOutlineDraw', 'Outline Draw'],
    ],
    'Advanced': [
      ['liquidDistortion', 'Liquid Distortion'], ['morphingText', 'Morphing Text'], ['fractureShatter', 'Fracture & Shatter'],
      ['burnAway', 'Burn Away'], ['dissolveNoise', 'Dissolve'], ['animatedTextureFill', 'Animated Texture Fill'],
      ['edgeExtrudeLighting', 'Edge Extrude + Light'], ['reflectionRefraction', 'Reflection & Refraction'],
    ],
  }

  const toggleEffect = (type) => {
    const next = { ...template_content }
    for (const p of next.pages || []) {
      const idx = (p.items || []).findIndex((it) => it.id === selectedElement)
      if (idx >= 0) {
        const it = p.items[idx]
        const list = Array.isArray(it.textEffects) ? [...it.textEffects] : []
        const existing = list.findIndex((e) => e.type === type)
        if (existing >= 0) {
          list.splice(existing, 1)
        } else {
          list.push({ type, enabled: true, ...getEffectDefaults(type) })
        }
        it.textEffects = list
        break
      }
    }
    settemplate_content(next)
  }

  const clearAll = () => {
    const next = { ...template_content }
    for (const p of next.pages || []) {
      const idx = (p.items || []).findIndex((it) => it.id === selectedElement)
      if (idx >= 0) { p.items[idx].textEffects = []; break }
    }
    settemplate_content(next)
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800">Text Effects</h2>
        <div className="flex items-center gap-2">
          {appliedTypes.length > 0 && (
            <button onClick={clearAll} className="px-2 py-1 text-xs bg-red-100 text-red-600 hover:bg-red-200 rounded-md transition-colors">
              Clear All
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-md transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="px-4 pt-3">
        <p className="text-[11px] text-gray-500">
          Effects stack — click to add or remove. {appliedTypes.length} active. Edit each
          effect's settings in the properties panel.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {Object.entries(categories).map(([cat, effects]) => (
          <div key={cat}>
            <h3 className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">{cat}</h3>
            <div className="grid grid-cols-2 gap-2">
              {effects.map(([type, label]) => {
                const active = appliedTypes.includes(type)
                return (
                  <button
                    key={type}
                    onClick={() => toggleEffect(type)}
                    onMouseEnter={() => setHovered(type)}
                    onMouseLeave={() => setHovered(null)}
                    className={`text-left rounded-lg px-3 py-2 text-sm transition-all border-2 ${
                      active ? 'border-green-400 bg-green-50 text-gray-800'
                        : hovered === type ? 'border-indigo-400 bg-gray-50'
                        : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-indigo-300'
                    }`}
                  >
                    <span className="flex items-center justify-between gap-1">
                      <span className="truncate">{label}</span>
                      {active && <span className="text-green-500 text-xs shrink-0">✓</span>}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
        <p className="text-xs text-gray-500">Effects render in both preview and exported video.</p>
      </div>
    </div>
  )
}
